import base64url from 'base64url';
import {
  MRT_ColumnFilterFnsState,
  MRT_ColumnFiltersState,
  MRT_ColumnOrderState,
  MRT_ColumnSizingState,
  MRT_GroupingState,
  MRT_SortingState,
  MRT_VisibilityState,
} from 'material-react-table';

import { parseDateValue } from './tableHelpers';

interface ShareState {
  market: string;
  sorting: MRT_SortingState;
  columnFilterFns: MRT_ColumnFilterFnsState;
  columnFilters: MRT_ColumnFiltersState;
  columnSizing: MRT_ColumnSizingState;
  columnVisibility: MRT_VisibilityState;
  columnOrder: MRT_ColumnOrderState;
  grouping: MRT_GroupingState;
}

export function decodeSharedParam(
  shared?: string | null,
): Record<string, unknown> | null | undefined {
  if (!shared) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(base64url.decode(shared));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function canRestoreSharedState({
  isLoading,
  hasPermission,
  hasColumns,
}: {
  isLoading: boolean;
  hasPermission: boolean;
  hasColumns: boolean;
}): boolean {
  return !isLoading && hasPermission && hasColumns;
}

export function isColumnSizingState(
  value: unknown,
): value is MRT_ColumnSizingState {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getRestorableColumnSizing(
  columnSizing: unknown,
  availableColumns: string[],
): MRT_ColumnSizingState {
  if (!isColumnSizingState(columnSizing)) {
    return {};
  }

  return Object.entries(columnSizing).reduce<MRT_ColumnSizingState>(
    (restoredSizing, [columnId, size]) => {
      if (availableColumns.includes(columnId) && typeof size === 'number') {
        restoredSizing[columnId] = size;
      }

      return restoredSizing;
    },
    {},
  );
}

export function mergeRestorableColumnSizing(
  currentSizing: MRT_ColumnSizingState,
  restoredSizing: MRT_ColumnSizingState,
): MRT_ColumnSizingState {
  return { ...currentSizing, ...restoredSizing };
}

export function hydrateSharedDateFilters(
  columnFilters: MRT_ColumnFiltersState,
  columnTypes?: Record<string, unknown>,
): MRT_ColumnFiltersState {
  return columnFilters.map((filter) => {
    const columnType = columnTypes?.[filter.id];
    if (columnType !== 'date' && columnType !== 'datetime') {
      return filter;
    }

    const hydrateValue = (value: unknown) =>
      value instanceof Date ? value : parseDateValue(value);

    return {
      ...filter,
      value: Array.isArray(filter.value)
        ? filter.value.map(hydrateValue)
        : hydrateValue(filter.value),
    };
  });
}

export function buildShareUrl({
  market,
  sorting,
  columnFilterFns,
  columnFilters,
  columnSizing,
  columnVisibility,
  columnOrder,
  grouping,
}: ShareState): string {
  const url = new URL(window.location.href);

  const payload = JSON.stringify(
    {
      [market]: {
        sorting,
        columnFilterFns,
        columnFilters,
        columnSizing,
        columnVisibility,
        columnOrder,
        grouping,
      },
    },
    (_, v) => {
      if (Array.isArray(v) && v.every((o) => JSON.stringify(o) === '{}')) {
        return undefined;
      }
      return JSON.stringify(v) === '{}' ? undefined : v;
    },
  );

  const shared = base64url(payload);
  if (shared) url.searchParams.set('shared', shared);

  return url.href;
}
